import { Cancel, CheckCircle } from "@mui/icons-material";
import { Button, Grid, Paper, Stack, Typography } from "@mui/material";
import React from "react";
import { ProjectBillingDetailsDto, ProjectMemberUpdateDto } from "../../../types/projectTypes";
import CheckDocumentButton from "./CheckDocumentButton";
import dayjs from "dayjs";
import useResponsive from "../../../hooks/useResponsive";

/**
 * Interface for Project Document Details
 */
interface ProjectDocumentDetailsProps {
  projectBillingDetailsDto: ProjectBillingDetailsDto;
  hasPermissionContractFiling: boolean;
  hasPermissionDocumentFiling: boolean;
  hasPermissionProjectBilling: boolean;
  changeProjectBillingDocumentStatus: (
    documentType:
      | "APatEV"
      | "APHold"
      | "evaluationAtEV"
      | "DLatEV"
      | "offerInAlfresco"
      | "deviationFromStandard"
      | "consultingContractProvided"
      | "teamContractProvided"
      | "qmApproval"
      | "paymentDelay"
      | "moneyReceived",
    completed: boolean
  ) => Promise<void>;
  changeProjectMemberBillingDetails: (memberId: number, billingDocument: ProjectMemberUpdateDto) => Promise<void>;
}

/**
 * Project Document Details Component to display the document details of a project
 * @param projectBillingDetailsDto - project billing details
 * @param hasPermissionContractFiling - boolean if the user has permission to file contract documents
 * @param hasPermissionDocumentFiling - boolean if the user has permission to file documents
 * @param hasPermissionProjectBilling - boolean if the user has permission to project billing
 * @param changeProjectBillingDocumentStatus - function to change the status of a project billing document
 * @param changeProjectMemberBillingDetails - function to change the status of a project member billing document
 * @returns Project Document Details Component
 */
const ProjectDocumentDetails = ({
  projectBillingDetailsDto,
  hasPermissionContractFiling,
  hasPermissionDocumentFiling,
  hasPermissionProjectBilling,
  changeProjectBillingDocumentStatus,
  changeProjectMemberBillingDetails,
}: ProjectDocumentDetailsProps) => {
  const isMobile = useResponsive("down", "sm");

  return (
    <Paper sx={{ p: 2 }}>
      <Grid container rowGap={1}>
        <Grid item container alignItems={"center"}>
          <Grid item xs={6}>
            <Typography textAlign={"end"}>
              Abschlusspräsentation, Referenzslide und Websitereferenz vorhanden
            </Typography>
          </Grid>
          <Grid item xs={1} sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            {projectBillingDetailsDto.APatEV ? <CheckCircle color="success" /> : <Cancel color="error" />}
          </Grid>
          <Grid item xs={4}>
            {!hasPermissionDocumentFiling ? null : (
              <CheckDocumentButton
                documentType="APatEV"
                checked={projectBillingDetailsDto.APatEV}
                changeProjectBillingDocumentStatus={changeProjectBillingDocumentStatus}
              />
            )}
          </Grid>
        </Grid>
        <Grid item container alignItems={"center"}>
          <Grid item xs={6}>
            <Typography textAlign={"end"}>Abschlusspräsentation gehalten</Typography>
          </Grid>
          <Grid item xs={1} sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            {projectBillingDetailsDto.APHold ? <CheckCircle color="success" /> : <Cancel color="error" />}
          </Grid>
          <Grid item xs={4}>
            {!hasPermissionDocumentFiling ? null : (
              <CheckDocumentButton
                documentType="APHold"
                checked={projectBillingDetailsDto.APHold}
                changeProjectBillingDocumentStatus={changeProjectBillingDocumentStatus}
              />
            )}
          </Grid>
        </Grid>
        <Grid item container alignItems={"center"}>
          <Grid item xs={6}>
            <Typography textAlign={"end"}>Evaluationsergebnisse vorhanden</Typography>
          </Grid>
          <Grid item xs={1} sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            {projectBillingDetailsDto.evaluationAtEV ? <CheckCircle color="success" /> : <Cancel color="error" />}
          </Grid>
          <Grid item xs={4}>
            {!hasPermissionDocumentFiling ? null : (
              <CheckDocumentButton
                documentType="evaluationAtEV"
                checked={projectBillingDetailsDto.evaluationAtEV}
                changeProjectBillingDocumentStatus={changeProjectBillingDocumentStatus}
              />
            )}
          </Grid>
        </Grid>
        <Grid item container alignItems={"center"}>
          <Grid item xs={6}>
            <Typography textAlign={"end"}>Dokumentationsleitfaden vorhanden</Typography>
          </Grid>
          <Grid item xs={1} sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            {projectBillingDetailsDto.DLatEV ? <CheckCircle color="success" /> : <Cancel color="error" />}
          </Grid>
          <Grid item xs={4}>
            {!hasPermissionDocumentFiling ? null : (
              <CheckDocumentButton
                documentType="DLatEV"
                checked={projectBillingDetailsDto.DLatEV}
                changeProjectBillingDocumentStatus={changeProjectBillingDocumentStatus}
              />
            )}
          </Grid>
        </Grid>
        <Grid item container alignItems={"center"}>
          <Grid item xs={6}>
            <Typography textAlign={"end"}>Angebot in Alfresco vorhanden</Typography>
          </Grid>
          <Grid item xs={1} sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            {projectBillingDetailsDto.offerInAlfresco ? <CheckCircle color="success" /> : <Cancel color="error" />}
          </Grid>
          <Grid item xs={4}>
            {!hasPermissionDocumentFiling ? null : (
              <CheckDocumentButton
                documentType="offerInAlfresco"
                checked={projectBillingDetailsDto.offerInAlfresco}
                changeProjectBillingDocumentStatus={changeProjectBillingDocumentStatus}
              />
            )}
          </Grid>
        </Grid>
        <Grid item container alignItems={"center"}>
          <Grid item xs={6}>
            <Typography textAlign={"end"}>Abweichung vom Standard</Typography>
          </Grid>
          <Grid item xs={1} sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            {projectBillingDetailsDto.deviationFromStandard ? (
              <CheckCircle color="success" />
            ) : (
              <Cancel color="error" />
            )}
          </Grid>
          <Grid item xs={4}>
            {!hasPermissionDocumentFiling ? null : (
              <CheckDocumentButton
                documentType="deviationFromStandard"
                checked={projectBillingDetailsDto.deviationFromStandard}
                changeProjectBillingDocumentStatus={changeProjectBillingDocumentStatus}
              />
            )}
          </Grid>
        </Grid>
        <Grid item container alignItems={"center"}>
          <Grid item xs={6}>
            <Typography textAlign={"end"}>Beratungsvertrag vorhanden</Typography>
          </Grid>
          <Grid item xs={1} sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            {projectBillingDetailsDto.consultingContractProvided ? (
              <CheckCircle color="success" />
            ) : (
              <Cancel color="error" />
            )}
          </Grid>
          <Grid item xs={4}>
            {!hasPermissionContractFiling ? null : (
              <CheckDocumentButton
                documentType="consultingContractProvided"
                checked={projectBillingDetailsDto.consultingContractProvided}
                changeProjectBillingDocumentStatus={changeProjectBillingDocumentStatus}
              />
            )}
          </Grid>
        </Grid>
        <Grid item container alignItems={"center"}>
          <Grid item xs={6}>
            <Typography textAlign={"end"}>Teamvertrag vorhanden</Typography>
          </Grid>
          <Grid item xs={1} sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            {projectBillingDetailsDto.teamContractProvided ? <CheckCircle color="success" /> : <Cancel color="error" />}
          </Grid>
          <Grid item xs={4}>
            {!hasPermissionContractFiling ? null : (
              <CheckDocumentButton
                documentType="teamContractProvided"
                checked={projectBillingDetailsDto.teamContractProvided}
                changeProjectBillingDocumentStatus={changeProjectBillingDocumentStatus}
              />
            )}
          </Grid>
        </Grid>
        <Grid item container alignItems={"center"}>
          <Grid item xs={6}>
            <Typography textAlign={"end"}>QM-Freigabe</Typography>
          </Grid>
          <Grid item xs={1} sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            {projectBillingDetailsDto.qmApproval ? <CheckCircle color="success" /> : <Cancel color="error" />}
          </Grid>
          <Grid item xs={4}>
            {!hasPermissionDocumentFiling ? null : (
              <CheckDocumentButton
                documentType="qmApproval"
                checked={projectBillingDetailsDto.qmApproval}
                changeProjectBillingDocumentStatus={changeProjectBillingDocumentStatus}
              />
            )}
          </Grid>
        </Grid>
        <Grid item container alignItems={"center"}>
          <Grid item xs={6}>
            <Typography textAlign={"end"}>Zahlungsverzug</Typography>
          </Grid>
          <Grid item xs={1} sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            {projectBillingDetailsDto.paymentDelay ? <CheckCircle color="success" /> : <Cancel color="error" />}
          </Grid>
          <Grid item xs={4}>
            {!hasPermissionProjectBilling ? null : (
              <CheckDocumentButton
                documentType="paymentDelay"
                checked={projectBillingDetailsDto.paymentDelay}
                changeProjectBillingDocumentStatus={changeProjectBillingDocumentStatus}
              />
            )}
          </Grid>
        </Grid>
        <Grid item container alignItems={"center"}>
          <Grid item xs={6}>
            <Typography textAlign={"end"}>Geld eingegangen</Typography>
          </Grid>
          <Grid item xs={1} sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            {projectBillingDetailsDto.moneyReceived ? <CheckCircle color="success" /> : <Cancel color="error" />}
          </Grid>
          <Grid item xs={4}>
            {!hasPermissionProjectBilling ? null : (
              <CheckDocumentButton
                documentType="moneyReceived"
                checked={projectBillingDetailsDto.moneyReceived}
                changeProjectBillingDocumentStatus={changeProjectBillingDocumentStatus}
              />
            )}
          </Grid>
        </Grid>
        {projectBillingDetailsDto.membersBilling.map((memberBilling) => (
          <Grid item container rowGap={1} alignItems={"center"} key={memberBilling.memberId}>
            <Grid item xs={6}>
              <Typography textAlign={"end"}>
                Vertrag freie Mitarbeit erhalten von {memberBilling.firstname} {memberBilling.lastname}
              </Typography>
            </Grid>
            <Grid item xs={1} sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
              {memberBilling.freelancerContract ? <CheckCircle color="success" /> : <Cancel color="error" />}
            </Grid>
            <Grid item xs={4}>
              {memberBilling.freelancerContract ? (
                <Stack direction={isMobile ? "column" : "row"} alignItems={"center"} spacing={isMobile ? 1 : 3}>
                  <Typography fontSize={14}>
                    ({dayjs(memberBilling.freelancerContract).format("DD.MM.YYYY")})
                  </Typography>
                  {hasPermissionContractFiling ? (
                    <Button
                      variant="contained"
                      color="secondary"
                      size="small"
                      onClick={() => {
                        changeProjectMemberBillingDetails(memberBilling.memberId, {
                          documentType: "freelancerContract",
                          completed: false,
                        });
                      }}
                    >
                      {isMobile ? null : <Cancel color="error" sx={{ marginRight: 2 }} fontSize="small" />}
                      <Typography fontSize={10}>Rückgängig</Typography>
                    </Button>
                  ) : null}
                </Stack>
              ) : hasPermissionContractFiling ? (
                <Button
                  sx={{ flex: 1 }}
                  variant="contained"
                  color="secondary"
                  size="small"
                  onClick={() => {
                    changeProjectMemberBillingDetails(memberBilling.memberId, {
                      documentType: "freelancerContract",
                      completed: true,
                    });
                  }}
                >
                  <CheckCircle color="success" sx={{ marginRight: 2 }} fontSize="small" />
                  <Typography fontSize={10}>Markieren</Typography>
                </Button>
              ) : null}
            </Grid>
            <Grid item xs={6}>
              <Typography textAlign={"end"}>
                Geld überwiesen an {memberBilling.firstname} {memberBilling.lastname}
              </Typography>
            </Grid>
            <Grid item xs={1} sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
              {memberBilling.moneyTransferred ? <CheckCircle color="success" /> : <Cancel color="error" />}
            </Grid>
            <Grid item xs={4}>
              {memberBilling.moneyTransferred ? (
                <Stack direction={isMobile ? "column" : "row"} alignItems={"center"} spacing={isMobile ? 1 : 3}>
                  <Typography fontSize={14}>({dayjs(memberBilling.moneyTransferred).format("DD.MM.YYYY")})</Typography>
                  {hasPermissionProjectBilling ? (
                    <Button
                      variant="contained"
                      color="secondary"
                      size="small"
                      onClick={() => {
                        changeProjectMemberBillingDetails(memberBilling.memberId, {
                          documentType: "moneyTransferred",
                          completed: false,
                        });
                      }}
                    >
                      {isMobile ? null : <Cancel color="error" sx={{ marginRight: 2 }} fontSize="small" />}
                      <Typography fontSize={10}>Rückgängig</Typography>
                    </Button>
                  ) : null}
                </Stack>
              ) : hasPermissionProjectBilling ? (
                <Button
                  sx={{ flex: 1 }}
                  variant="contained"
                  color="secondary"
                  size="small"
                  onClick={() => {
                    changeProjectMemberBillingDetails(memberBilling.memberId, {
                      documentType: "moneyTransferred",
                      completed: true,
                    });
                  }}
                >
                  <CheckCircle color="success" sx={{ marginRight: 2 }} fontSize="small" />
                  <Typography fontSize={10}>Markieren</Typography>
                </Button>
              ) : null}
            </Grid>
          </Grid>
        ))}
      </Grid>
    </Paper>
  );
};

export default ProjectDocumentDetails;
